import chroma from "chroma-js";
import classNames from "classnames";
import { Appearance } from "common/generator/components/button-appearance";
import type { WireButtonsComponent } from "common/generator/components/fluent-components";
import { isDefined, isEmptyOrUndefinedish } from "common/Support";
import { UIBackgroundStyle } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import * as React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import tw from "twin.macro";
import { defaultWireAppAccentContextOverlay } from "webapp/lib/theme/core-themes/wireapp-theme";
import { styled } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { SectionStyleProvider } from "webapp/ui/views/wire-container/wire-container";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";

const ButtonBarStyle = styled.div`
    ${tw`relative flex flex-col items-center p-8 text-center rounded-xl shadow-md text-text-dark`}

    &.none {
        ${tw`p-0 shadow-none`};
    }

    &.soft {
        ${tw`shadow-none`};
        background-color: ${p => chroma(p.theme.accent).alpha(0.1).css()};
    }

    &.card {
        ${tw`bg-bg-front`};
        > h2 {
            ${tw`text-text-dark`}
        }
        > h3 {
            ${tw`text-text-pale`}
        }
    }

    &.start {
        ${tw`items-start text-left`};

        .button-box {
            justify-content: flex-start;
        }
    }

    &.end {
        ${tw`items-end text-right`};

        .button-box {
            justify-content: flex-end;
        }
    }

    &.stretch {
        ${tw`items-stretch text-left`};

        .button-box {
            justify-content: stretch;
        }
    }

    &.wide {
        button {
            flex-grow: 1;
            flex-basis: 0;
            max-width: 50%;
        }
    }
`;

export const WireButtonBar: WireRenderer<WireButtonsComponent, { overrideAppearance?: Appearance }> = React.memo(p => {
    const toRender = p.buttons.filter(b => isDefined(b.action) && !isEmptyOrUndefinedish(b.title));

    const newStyle = p.background;

    let theme = useWireAppTheme();
    if (p.background === UIBackgroundStyle.Accent) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlay]);
    }

    return (
        <SectionStyleProvider value={newStyle}>
            <TailwindThemeProvider theme={theme}>
                <ButtonBarStyle
                    className={classNames(
                        p.alignment,
                        p.size,
                        p.background,
                        p.background === UIBackgroundStyle.Accent && "accent-bg"
                    )}>
                    {p.title && <h2 tw="text-2xl font-extrabold mb-2 text-text-contextual-dark">{p.title}</h2>}
                    {p.description && <h3 tw="mb-6 text-base text-text-contextual-pale">{p.description}</h3>}
                    <div tw="flex self-stretch justify-center flex-wrap row-gap[12px]" className="button-box">
                        {toRender.map((b, i) => {
                            const { action } = b;
                            if (!isDefined(action)) return undefined;
                            const appearance =
                                p.overrideAppearance ?? (i === 0 ? Appearance.Filled : Appearance.Bordered);
                            return (
                                <WireButton
                                    key={i}
                                    data-testid="wbb-button"
                                    onClick={() => p.backend.runAction(action.token, false)}
                                    tw="not-last:mr-3"
                                    appearance={appearance}>
                                    {b.title}
                                </WireButton>
                            );
                        })}
                    </div>
                </ButtonBarStyle>
            </TailwindThemeProvider>
        </SectionStyleProvider>
    );
});
